import { PlusOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import React, { useEffect, useState } from 'react';

import { Button, Checkbox, Col, DatePicker, Form, Input, Modal, Row, Select } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import Title from 'antd/es/typography/Title';
import { APP_LOCAL_DATE_FORMAT, APP_LOCAL_DATE_FORMAT_PAYLOAD, ObjectiveSymbol } from 'app/config/constants';
import { getAssociates, getBoxWorkType, getFrequencyType, getSelectBoxWorkPriorityLevel } from 'app/entities/common/common.reducer';
import { getEntities as getFields } from 'app/entities/field/field.reducer';
import { getEntities as getPlanPeriods } from 'app/entities/plan-period/plan-period.reducer';
import { getEntities as getProjects } from 'app/entities/project/project.reducer';
import { filterOption } from 'app/shared/util/select-filter';
import dayjs from 'dayjs';
import * as _ from 'lodash';
import { Container } from 'reactstrap';
import { getOnlyPersonInOrg as getEmployees } from 'app/entities/common/common.reducer';
import {
  createEntity,
  createEntityAndCreateOther,
  getEntity,
  reset,
  updateEntity,
  updateEntityAndCreateOther
} from '../activities.reducer';
import ObjectiveKRRowSelection from './view-row-selection/objective-kr';

type ActivitiesFormType = {
  isOpen: boolean;
  onClose: (isReload?: boolean) => void;
  id?: string;
  objectiveSelected?: any;
  krSelected?: any;
  isView?: boolean;
};

export const ActivitiesUpdate = ({ isOpen, onClose, id, objectiveSelected, krSelected, isView = false }: ActivitiesFormType) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const isNew = id === undefined || id === null;

  const activitiesEntity = useAppSelector(state => state.activities.entity);
  const workTypeList = useAppSelector(state => state.common.workType);
  const frequencies = useAppSelector(state => state.common.frequencies);
  const projectList = useAppSelector(state => state.project.entities);
  const employeeList = useAppSelector(state => state.common.personPositions);
  const fieldList = useAppSelector(state => state.field.entities);
  const updating = useAppSelector(state => state.activities.updating);
  const updateSuccess = useAppSelector(state => state.activities.updateSuccess);
  const priorityLevelList = useAppSelector(state => state.common.priorityLevel);

  const [isOpenModal, setOpenModal] = useState(false);
  const [parentObjectiveIdSelected, setParentObjectiveIdSelected] = useState(null);
  const [parentKrIdSelected, setParentKrIdSelected] = useState(null);
  const [alignSelected, setAlignmentSelected] = useState(null);

  const handleClose = () => {
    form.resetFields();
    onClose(true);
  };

  useEffect(() => {
    innitData();
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset())
      handleClose();
    }
  }, [updateSuccess]);

  useEffect(() => {
    if (objectiveSelected) {
      setParentObjectiveIdSelected(objectiveSelected.id)
      form.setFieldsValue({ ['alignCode']: objectiveSelected?.code });
    }
  }, [objectiveSelected]);

  useEffect(() => {
    if (krSelected) {
      setParentKrIdSelected(objectiveSelected.id)
      form.setFieldsValue({ ['alignCode']: krSelected?.code });
    }
  }, [krSelected]);

  useEffect(() => {
    if (activitiesEntity && !isNew) {
      if (activitiesEntity?.alignCode) {
        if (_.startsWith(activitiesEntity?.alignCode.code, ObjectiveSymbol, 0)) {
          setAlignmentSelected([activitiesEntity?.parentObjectiveId])
        }
        else {
          setAlignmentSelected([activitiesEntity?.parentKrId])
        }
      }
      form.setFieldsValue({
        ...activitiesEntity,
        startDate: dayjs(activitiesEntity.startDate),
        dueDate: dayjs(activitiesEntity.dueDate),
        tags: _.join(activitiesEntity.tags, ','),
        informEmails: activitiesEntity.raci?.informEmails,
        consultantIds: activitiesEntity.raci?.consultantIds,
        accountableId: activitiesEntity.raci?.accountableId,
        responsibleIds: activitiesEntity.raci?.responsibleIds,
      });
    }
  }, [activitiesEntity]);

  const innitData = () => {
    dispatch(getSelectBoxWorkPriorityLevel())
    dispatch(getBoxWorkType());
    dispatch(getAssociates());
    dispatch(getFields({}));
    dispatch(getEmployees());
    dispatch(getPlanPeriods({}));
    dispatch(getProjects({}));
    dispatch(getFrequencyType(null));
    if (isNew) {
      form.resetFields();
    } else {
      dispatch(getEntity(id));
    }
  };

  // eslint-disable-next-line complexity
  const handleSubmit = values => {
    const entity = mapperEntityPayload(values);
    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const handleSubmitAndNavigateCreateKr = () => {
    form
      .validateFields()
      .then(values => {
        const entity = mapperEntityPayload(values);
        if (isNew) {
          dispatch(createEntityAndCreateOther(entity))
            .then(() => {
              form.resetFields();
            })
            .catch(() => {
              return;
            });
        } else {
          dispatch(updateEntityAndCreateOther(entity));
        }
      })
      .catch(() => {
        return;
      });
  };

  const mapperEntityPayload = values => {
    const {
      tags,
      planTags,
      contribution,
      informEmails,
      consultantIds,
      accountableId,
      responsibleIds,
      alignCode,
      ...remainingValues
    } = values;
    const entity = {
      ...activitiesEntity,
      ...remainingValues,
      startDate: dayjs(remainingValues.startDate).format(APP_LOCAL_DATE_FORMAT_PAYLOAD),
      dueDate: dayjs(remainingValues.dueDate).format(APP_LOCAL_DATE_FORMAT_PAYLOAD),
      parentKrId: parentKrIdSelected,
      parentObjectiveId: parentObjectiveIdSelected,
      raci: {
        informEmails: _.split(informEmails, ','),
        consultantIds,
        accountableId,
        responsibleIds,
      },
      tags: _.split(tags, ','),
      planTags: _.split(planTags, ','),
    };
    return entity;
  };

  const handleOpenModalViewRowSelection = () => {
    setOpenModal(true)
  }

  const handleCloseModal = (isUpdateValue = true, itemSelected = []) => {
    if (isUpdateValue) {
      if (itemSelected.length > 0) {
        const selectedItem = itemSelected[0];
        if (_.startsWith(selectedItem.code, ObjectiveSymbol, 0)) {
          setParentObjectiveIdSelected(selectedItem.id)
        }
        else {
          setParentKrIdSelected(selectedItem.id)
        }
      }
      else {
        setParentObjectiveIdSelected(null);
        setParentKrIdSelected(null);
      }
      form.setFieldValue('alignCode', itemSelected.length > 0 ? `${_.join(itemSelected.map(x => x.code), " ,")}` : null);
    }
    setOpenModal(false)
  }

  return (
    <Modal
      open={isOpen}
      maskClosable={false}
      onCancel={() => {
        onClose();
      }}
      footer={null}
      width={1300}
    >
      <Container className="form">
        <Form size="large" form={form} disabled={isView} onFinish={handleSubmit} layout="vertical">
          <Title level={3}>{`${id ? 'Cập nhật thông tin' : 'Thêm mới'} công việc`}</Title>
          <Row>
            <Col md={5}>
              <Form.Item
                label="KR liên kết"
                name={'alignCode'}
              >
                <Row gutter={8} align="middle">
                  <Col flex="auto">
                    <Form.Item noStyle
                      name={'alignCode'}>
                      <Input
                        readOnly
                        size="large"
                        placeholder="Chọn mục tiêu, KR liên kết"
                      />
                    </Form.Item>
                  </Col>
                  <Col>
                    <Button
                      className="primary"
                      size="large"
                      onClick={handleOpenModalViewRowSelection}
                      icon={<PlusOutlined />}
                    >
                    </Button>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
            <Col md={1} />
            <Col md={4}>
              <Form.Item label="Lĩnh vực" name="field">
                <Select
                  allowClear
                  showSearch
                  size="large"
                  filterOption={filterOption}
                  options={fieldList.map(x => {
                    return {
                      label: x.description,
                      value: x.value,
                    };
                  })}
                  placeholder="Chọn lĩnh vực"
                />
              </Form.Item>
            </Col>
            <Col md={1} />
            <Col md={4}>
              <Form.Item label="Dự án" name="projectId">
                <Select
                  filterOption={filterOption}
                  allowClear
                  showSearch
                  size="large"
                  options={projectList.map(x => {
                    return {
                      label: x.name,
                      value: x.id,
                    };
                  })}
                  placeholder="Chọn dự án"
                />
              </Form.Item>
            </Col>
            <Col md={1} />
            <Col md={4}>
              <Form.Item label="Mảng công việc" name="workTypeId">
                <Select
                  filterOption={filterOption}
                  allowClear
                  showSearch
                  size="large"
                  options={workTypeList.map(x => {
                    return {
                      label: x.name,
                      value: x.value,
                    };
                  })}
                  placeholder="Chọn nhóm công việc"
                />
              </Form.Item>
            </Col>
            <Col md={1} />
            <Col md={3}>
              <Form.Item label="Mức độ ưu tiên" rules={[{ required: true, message: 'Hãy chọn mức độ ưu tiên' }]} name="workPriorityLevel">
                <Select
                  filterOption={filterOption}
                  allowClear
                  showSearch
                  size="large"
                  options={priorityLevelList}
                  placeholder="Chọn mức độ ưu tiên"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col md={24}>
              <Form.Item label="Nội dung công việc" rules={[{ required: true, message: 'Hãy nhập nội dung công việc' }]} name={'purpose'}>
                <TextArea placeholder="Nhập nội dung công việc" size="large" />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col md={7}>
              <Form.Item label="Ngày bắt đầu" rules={[{ required: true, message: 'Hãy chọn ngày bắt đầu' }]} name="startDate">
                <DatePicker className="w-100" format={APP_LOCAL_DATE_FORMAT} placeholder="Chọn ngày bắt đầu" />
              </Form.Item>
            </Col>
            <Col md={1} />
            <Col md={7}>
              <Form.Item label="Thời hạn hoàn thành" rules={[{ required: true, message: 'Hãy chọn thời hạn hoàn thành' }]} name="dueDate">
                <DatePicker className="w-100" format={APP_LOCAL_DATE_FORMAT} placeholder="Chọn thời hạn hoàn thành" />
              </Form.Item>
            </Col>
            <Col md={1} />
            <Col md={8}>
              <Form.Item
                label="Tần suất báo cáo"
                name="frequencyId"
                rules={[{ required: true, message: 'Hãy chọn tần suất báo cáo' }]}
              >
                <Select
                  filterOption={filterOption}
                  allowClear
                  showSearch
                  size="large"
                  options={frequencies}
                  placeholder="Chọn tần suất báo cáo"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col md={7}>
              <Form.Item
                label="Người giao việc"
                rules={[{ required: true, message: 'Hãy chọn người giao việc' }]}
                name="assignedById"
              >
                <Select
                  filterOption={filterOption}
                  allowClear
                  showSearch
                  size="large"
                  options={employeeList.map(x => {
                    return {
                      label: x.name,
                      value: x.id,
                    };
                  })}
                  placeholder="Chọn người giao việc"
                />
              </Form.Item>
            </Col>
            <Col md={1} />
            <Col md={7}>
              <Form.Item
                label="Thực hiện chính (PIC)"
                rules={[{ required: true, message: 'Hãy chọn người thực hiện chính' }]}
                name="accountableId"
              >
                <Select
                  filterOption={filterOption}
                  allowClear
                  showSearch
                  size="large"
                  options={employeeList.map(x => {
                    return {
                      label: x.name,
                      value: x.id,
                    };
                  })}
                  placeholder="Chọn PIC"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col md={7}>
              <Form.Item label="Các bên tham gia thực hiện" name="responsibleIds">
                <Select
                  filterOption={filterOption}
                  allowClear
                  mode="multiple"
                  showSearch
                  size="large"
                  options={employeeList.map(x => {
                    return {
                      label: x.name,
                      value: x.id,
                    };
                  })}
                  placeholder="Chọn các bên tham gia thực hiện"
                />
              </Form.Item>
            </Col>
            <Col md={1} />
            <Col md={7}>
              <Form.Item label="Các bên hỗ trợ" name="consultantIds">
                <Select
                  filterOption={filterOption}
                  allowClear
                  mode="multiple"
                  showSearch
                  size="large"
                  options={employeeList.map(x => {
                    return {
                      label: x.name,
                      value: x.id,
                    };
                  })}
                  placeholder="Chọn các bên hỗ trợ"
                />
              </Form.Item>
            </Col>
            <Col md={1} />
            <Col md={8}>
              <Form.Item label="Phân quyền truy cập" name={'accessIds'}>
                <Select
                  filterOption={filterOption}
                  allowClear
                  showSearch
                  placeholder="Chọn người"
                  mode='multiple'
                  size="large"
                  options={employeeList.map(x => {
                    return {
                      label: x.name,
                      value: x.id,
                    };
                  })}
                />
              </Form.Item>

            </Col>
          </Row>

          <Row>
            <Col md={7}>
              <Form.Item label="Tag" name={'tags'}>
                <Input className="w-100" size="large" placeholder="Nhập tag" />
              </Form.Item>
            </Col>
            <Col md={1} />
            <Col md={7}>
              <Form.Item label="Plan tag" name={'planTags'}>
                <Input className="w-100" size="large" placeholder="Nhập plan tag" />
              </Form.Item>
            </Col>
            <Col md={1} />
            <Col md={8}>
              <Form.Item label="Người nhận thông báo (cách nhau dấu phẩy)" name={'informEmails'}>
                <TextArea rows={1} placeholder="Nhập người nhận thông báo" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col md={24}>
              <Checkbox>Gửi thông báo đến người nhận</Checkbox>
            </Col>
          </Row>
          <Form.Item>
            <Row className="action mt-3" justify="end">
              {isNew && (
                <Col>
                  <Button className="green" onClick={handleSubmitAndNavigateCreateKr} disabled={updating}>
                    Lưu & Tạo công việc khác
                  </Button>
                </Col>
              )}
              <Col>
                {!isView &&
                  <Button className="primary" htmlType="submit" disabled={updating}>
                    Lưu
                  </Button>
                }
              </Col>
              <Col>
                <Button
                  className="secondary"
                  onClick={() => {
                    handleClose();
                  }}
                  disabled={updating}
                >
                  Hủy
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Container>
      {isOpenModal &&
        <ObjectiveKRRowSelection defaultSelected={alignSelected} isOpen={isOpenModal} onClose={handleCloseModal} />
      }
    </Modal>
  );
};

export default ActivitiesUpdate;
